import { Card, CardContent } from "@/components/ui/card";
import { ChevronDown, ChevronUp } from "lucide-react";
import mic from '../../public/images/mic.svg';
import user from '../../public/images/user.svg';
import chat from '../../public/images/chat.svg';
import upArrow from '../../public/images/Icon wrap.svg';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import '../styles/feedback.css';
import { useState, useRef, useEffect } from "react";

interface Task {
  index: number;
  cefr_level: string;
  title: string;
  feedback: string;
  reflection: string;
  native_example: {
    available: boolean;
    text_fr: string;
  };
}

interface DisplayData {
  tasks: Task[];
}

interface TaskAnalysisProps {
  isGuest: boolean;
  displayData: DisplayData;
  taskData: string;
}

const toTitleCase = (str: string) => {
  return str
    .toLowerCase()
    .split(" ")
    .map((word, index) => {
      if (word === "and" && index !== 0) return "and";
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

const TaskAnalysis = ({ isGuest, displayData, taskData }: TaskAnalysisProps) => {
  const taskImages = [mic, user, chat];
  const [openTaskIndex, setOpenTaskIndex] = useState<number | null>(null);
  const taskRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  const toggleTask = (idx: number) => {
    setOpenTaskIndex(openTaskIndex === idx ? null : idx);
  };

  useEffect(() => {
    if (openTaskIndex !== null && taskRefs.current[openTaskIndex]) {
      // Small delay to ensure the accordion close animation has started/finished
      // so the scroll position calculation is more accurate
      setTimeout(() => {
        taskRefs.current[openTaskIndex]?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 350);
    }
  }, [openTaskIndex]);

  return (
    <div className="task-analysis-grid ">
      {displayData.tasks.map((task, idx) => (
        <div
          key={task.index}
          ref={(el) => {
            taskRefs.current[idx] = el;
          }}
          className="scroll-mt-24" // Add scroll margin for sticky headers if any
        >
          <Card
            className="task-card-clickable"
            onClick={() => toggleTask(idx)}
          >
            <CardContent className="task-analysis-card-content p-0 border-0">
              <Collapsible
                className="task-analysis-collapsible"
                open={openTaskIndex === idx}
                onOpenChange={(isOpen) => setOpenTaskIndex(isOpen ? idx : null)}
              >
                <CollapsibleTrigger asChild>
                  <div className="task-analysis-header">
                    <div className="flex items-center" style={{ gap: '16px' }}>
                      <div className="img-task-analysis">
                        <img src={taskImages[idx % taskImages.length]} alt="" />
                      </div>

                      <div className="task-analysis-title-section">
                        <div className="task-analysis-title-inner">
                          <span className="feedback-task-title">
                            {toTitleCase(task.title)}
                          </span>
                        </div>

                        {/* Only show CEFR badge if task data is available */}
                        {/* {!taskData.includes(task.index.toString()) && (
                          <div
                            className={`feedback-task-title-budge ${task.cefr_level} ${isGuest ? 'blur-sm' : ''}`}
                          >
                            <span>{task.cefr_level}</span>
                          </div>
                        )} */}
                      </div>
                    </div>

                    {/* Arrow button */}
                    <button
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleTask(idx);
                      }}
                    >
                      {openTaskIndex === idx ? (
                        <ChevronUp className="h-4 w-4 shrink-0 transition-transform duration-200" />
                      ) : (
                        <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
                      )}
                    </button>
                  </div>
                </CollapsibleTrigger>

                <CollapsibleContent className="task-analysis-collapsible-content">
                  <div className="task-analysis-content">
                    <div className="task-analysis-content-inner">

                      {task.feedback && (
                        <div className="task-analysis-improved">
                          <div><h4>Feedback</h4></div>
                          <div className={`${isGuest ? 'blur-sm blur-fade' : ''}`}>
                            <p>{task.feedback}</p>
                          </div>
                        </div>
                      )}

                      {/* {task.reflection && (
                        <div className="task-analysis-improved">
                          <div><h4>Reflection</h4></div>
                          <div className={`${isGuest ? 'blur-sm blur-fade' : ''}`}>
                            <p>{task.reflection}</p>
                          </div>
                        </div>
                      )} */}

                      {/* Native Example Accordion */}
                      {task.native_example?.text_fr && (
                        <Collapsible
                          className="task-analysis-native-accordion"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <CollapsibleTrigger asChild>
                            <div
                              className="task-analysis-accordion-trigger cursor-pointer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <h4>Native Level Example</h4>
                              <img src={upArrow} alt="" />
                            </div>
                          </CollapsibleTrigger>

                          <CollapsibleContent className="task-analysis-accordion-content max-w-[1100px]">
                            <div
                              className={`task-analysis-accordion-inner max-w-[1150px] ${isGuest ? 'blur-sm blur-fade' : ''}`}
                            >
                              <p className="whitespace-pre-wrap">
                                {task.native_example.text_fr}
                              </p>
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      )}

                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </CardContent>
          </Card>
        </div>
      ))}
    </div>
  );
};

export default TaskAnalysis;
